export type AtsTargetSource = "onboarding" | "builder";

export type AtsTarget = {
  resumeId: string;
  targetJob: string;
  sector: string;
  applicationType: string | null;
  jobDescription: string;
  source: AtsTargetSource;
  savedAt: string;
};

const STORAGE_PREFIX = "moncvpro:ats-target:";

const getStorageKey = (resumeId: string) => `${STORAGE_PREFIX}${resumeId}`;

export const saveAtsTarget = (
  resumeId: string,
  target: Omit<AtsTarget, "resumeId" | "savedAt">,
) => {
  if (typeof window === "undefined" || !resumeId) {
    return;
  }

  const value: AtsTarget = {
    ...target,
    resumeId,
    targetJob: target.targetJob.trim(),
    sector: target.sector.trim(),
    jobDescription: target.jobDescription.trim(),
    savedAt: new Date().toISOString(),
  };

  window.localStorage.setItem(getStorageKey(resumeId), JSON.stringify(value));
};

export const getAtsTarget = (resumeId: string): AtsTarget | null => {
  if (typeof window === "undefined" || !resumeId) {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(getStorageKey(resumeId));

    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored) as Partial<AtsTarget>;

    if (parsed.resumeId !== resumeId) {
      return null;
    }

    return {
      resumeId,
      targetJob: String(parsed.targetJob ?? ""),
      sector: String(parsed.sector ?? ""),
      applicationType: parsed.applicationType ? String(parsed.applicationType) : null,
      jobDescription: String(parsed.jobDescription ?? ""),
      source: parsed.source === "builder" ? "builder" : "onboarding",
      savedAt: String(parsed.savedAt ?? new Date().toISOString()),
    };
  } catch {
    return null;
  }
};

export const updateAtsJobDescription = (resumeId: string, jobDescription: string) => {
  const current = getAtsTarget(resumeId);

  saveAtsTarget(resumeId, {
    targetJob: current?.targetJob ?? "",
    sector: current?.sector ?? "",
    applicationType: current?.applicationType ?? null,
    jobDescription,
    source: current?.source ?? "builder",
  });
};

export const removeAtsTarget = (resumeId: string) => {
  if (typeof window === "undefined" || !resumeId) {
    return;
  }

  window.localStorage.removeItem(getStorageKey(resumeId));
};
